import React from 'react';
import Icon from 'components/AppIcon';

function CircularProgress({ percentage, size = 80, strokeWidth = 7 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="white" strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
    </svg>
  );
}

export default function MembershipCard({ memberPackage, loading, error }) {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-md)] animate-pulse min-h-[200px]">
        <div className="h-6 bg-white/20 rounded w-1/3 mb-4" />
        <div className="h-4 bg-white/20 rounded w-2/3 mb-2" />
        <div className="h-4 bg-white/20 rounded w-1/2 mb-2" />
        <div className="h-4 bg-white/20 rounded w-3/4" />
      </div>
    );
  }

  if (error && !memberPackage) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 flex items-center gap-3 min-h-[160px]">
        <Icon name="AlertCircle" size={20} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  if (!memberPackage) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 flex flex-col items-center justify-center gap-3 min-h-[200px]">
        <Icon name="PackageX" size={32} color="var(--color-muted-foreground)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No active membership found</p>
        <p className="text-xs text-[var(--color-muted-foreground)]/70 font-[var(--font-caption)]">Contact your gym to get a membership plan</p>
      </div>
    );
  }

  const today = new Date();
  const startDate = new Date(memberPackage?.startDate);
  const endDate = new Date(memberPackage?.endDate);
  const totalDays = Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)));
  const daysRemaining = Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24)));
  const elapsed = totalDays - daysRemaining;
  const progress = Math.min(100, Math.round((elapsed / totalDays) * 100));

  let status = 'Active';
  let statusColor = 'bg-green-400/20 text-green-200';
  if (daysRemaining <= 0) { status = 'Expired'; statusColor = 'bg-red-400/20 text-red-200'; }
  else if (daysRemaining <= 10) { status = 'Expiring Soon'; statusColor = 'bg-yellow-400/20 text-yellow-200'; }

  const showRenewCTA = daysRemaining <= 10;

  return (
    <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-[var(--radius-md)] p-5 text-white shadow-[var(--shadow-md)] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-white/70 text-xs font-medium font-[var(--font-caption)] uppercase tracking-wider">Current Plan</p>
            <h3 className="text-xl font-bold font-[var(--font-heading)] mt-0.5">{memberPackage?.packageName}</h3>
            <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full font-[var(--font-caption)] ${statusColor}`}>{status}</span>
          </div>
          <div className="relative flex-shrink-0">
            <CircularProgress percentage={progress} size={72} strokeWidth={6} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-white font-bold text-sm font-[var(--font-heading)] leading-none">{progress}%</span>
              <span className="text-white/60 text-[9px] font-[var(--font-caption)]">used</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            { label: 'Start Date', value: startDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
            { label: 'Expires', value: endDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
            { label: 'Days Left', value: daysRemaining <= 0 ? 'Expired' : daysRemaining },
          ]?.map((item) => (
            <div key={item?.label}>
              <p className="text-white/60 text-[10px] font-[var(--font-caption)] uppercase tracking-wider">{item?.label}</p>
              <p className="text-white font-bold font-[var(--font-heading)] text-sm mt-0.5">{item?.value}</p>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-white/70 text-xs font-[var(--font-caption)]">Plan Usage</span>
            <span className="text-white text-xs font-medium font-[var(--font-caption)]">{elapsed} of {totalDays} days</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {showRenewCTA && (
          <div className="mt-4 flex items-center gap-2 bg-white/20 rounded-[var(--radius-sm)] px-3 py-2">
            <Icon name="AlertCircle" size={14} color="#fff" />
            <span className="text-xs text-white font-medium font-[var(--font-caption)]">
              {daysRemaining <= 0 ? 'Membership expired — renew now' : `Only ${daysRemaining} days left — renew now`}
            </span>
            <button className="ml-auto text-xs font-bold text-white underline font-[var(--font-caption)] hover:no-underline transition-all">Renew →</button>
          </div>
        )}
      </div>
    </div>
  );
}
